import { Rank, type EnemyHookContext } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { carrierX, COMBAT_START, CYCLE, firstDelayAt, PHASE_SOCLE } from './colosse.cycle';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   B03 — LE COLOSSE. Boss du monde 3, et fin de la campagne.

   Le découpage du cycle et la justification de chaque phase vivent dans
   `colosse.cycle.ts`, partagé par le noyau et ses quatre sections.

   ── La décision de conception qui le définit ──
   Son noyau n'est PAS blindé. C'est le seul boss du jeu dans ce cas, et c'est
   ce qui le distingue vraiment du Héraut et de la Navette.

   Les deux premiers boss imposent un ordre : démonter, puis tuer. C'est la
   bonne pédagogie tant qu'il s'agit d'apprendre le mécanisme. Le troisième
   suppose la leçon acquise et rend le choix au joueur — il peut tout démonter,
   ne retirer que ce qui le gêne, ou aller droit au noyau sous le feu des quatre
   armes. Le Bastion, croisé tout au long du monde 3, aura préparé exactement
   cet arbitrage sur des cibles où se tromper ne coûte rien.

   Le risque assumé est qu'un build très offensif « saute » le combat en fondant
   le noyau. Ce n'est pas un défaut : c'est la récompense d'un build construit,
   et elle ne s'obtient qu'au dernier monde, sur le dernier boss. Un jeu qui
   interdit toutes les sorties par la force finit par n'en récompenser aucune.

   ── Points de vie ──
   Valeurs de BASE ; le monde 3 applique ×2,4.

   Déduites de la courbe de puissance comme celles de la Navette : 3 100 DPS
   attendus au niveau 20. 77 500 × 2,4 = 186 000 PV effectifs, soit ~60 s.

   Dix secondes de plus que les deux boss précédents, et c'est délibéré : c'est
   le dernier combat de la campagne, il a quatre sections à faire découvrir, et
   son intérêt est justement le temps laissé pour arbitrer. Un combat final
   expédié en trente secondes ne donne pas l'occasion de choisir quoi que ce
   soit.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Le hook de balancement.
 *
 *  Troisième et dernière échappatoire de ce type (PLAN.md §3.3). Comme pour les
 *  deux autres boss, aucune trajectoire du moteur ne sait « osciller puis
 *  s'immobiliser à un instant précis du cycle » : `Motion.Hover` oscille sans
 *  jamais s'arrêter, et c'est l'arrêt qui porte toute l'information ici.
 *
 *  ⚠️ Le hook ne touche QUE l'abscisse. Le porteur se poste à Y = +320 et n'en
 *  bouge plus, ce qui garantit STRUCTURELLEMENT l'invariant « aucune émission
 *  sous Y = -180 » (PLAN.md §6.6) : la section la plus basse est une aile à
 *  offY -70, soit Y = +250. */
function balancer(slot: number, age: number, dt: number, ctx: EnemyHookContext): void {
  const t = age - COMBAT_START;
  if (t < 0) return;
  void dt; // position calculée depuis l'âge, jamais intégrée
  ctx.x[slot] = carrierX(t);
}

export default defineEnemy({
  id: 'colosse',
  rank: Rank.Heavy,
  boss: true,
  hp: 52000,
  visualRadius: 104,
  score: 40000,
  /* 2,4 s : le télégraphe des élites (PLAN.md §6.5), plus long que celui des
     deux autres boss. Il y a quatre sections à compter et une silhouette
     asymétrique à lire — le joueur doit avoir vu que le dôme est seul avant de
     pouvoir décider s'il commence par lui. */
  telegraph: 2.4,
  drops: 20,
  motion: { kind: Motion.Hover, speed: 165, amplitude: 0, frequency: 0, holdY: 320 },
  emitter: {
    /* Le noyau tire un éventail statique large et lent, et c'est sa seule arme.
       Comme chez les deux autres boss, la menace VISÉE passe exclusivement par
       une section (le dôme) et disparaît avec elle : le joueur qui l'a détruite
       obtient un changement de nature du combat, pas une simple baisse de
       volume. */
    pattern: Pattern.Fan,
    every: CYCLE,
    count: 15,
    spreadDeg: 176,
    speed: 310,
    firstDelay: firstDelayAt(PHASE_SOCLE),
    family: Fam.EnemyRed,
  },

  /* ── Deux phases, dès le premier coup ──────────────────────────────────────
     ⚠️ Le Colosse n'a PAS `shieldedByParts` : son noyau est exposé d'emblée. Ses
     phases se déclenchent donc PENDANT que ses sections tirent encore — elles
     s'AJOUTENT à la pression coordonnée. On les garde donc SPARSES (grand `every`)
     pour ne pas combler la fenêtre de punition des sections : le noyau ajoute une
     NATURE de menace, pas du volume. D'abord des MISSILES guidés (il faut bouger),
     puis un pouls d'ANNEAUX en enrage. Seuils sur les 52 000 PV du corps. */
  phases: [
    {
      // Le cœur EXPOSÉ réagit à son démantèlement : dès 2 sections tombées OU 66 %
      // de PV (le premier des deux), il lance des missiles guidés, rares — une
      // menace qui te suit, reste mobile. Détruire tôt a désormais un prix.
      atHpFrac: 0.66,
      atPartsLost: 2,
      telegraph: 0.8,
      emitter: {
        pattern: Pattern.Homing,
        every: 3.0,
        count: 3,
        spreadDeg: 44,
        speed: 200,
        turnDeg: 55,
      },
    },
    {
      // Toutes sections tombées OU 33 % de PV : l'enrage. Des anneaux qui pulsent,
      // ET le Colosse INVOQUE un essaim de drones (jusqu'à 4, bornés) — la seule
      // ponte de boss du jeu, réservée à son moment le plus désespéré.
      atHpFrac: 0.33,
      atPartsLost: 4,
      telegraph: 1.0,
      speedMul: 1.2,
      emitter: { pattern: Pattern.Ring, every: 2.0, count: 12, speed: 250, stepDeg: 9 },
      brood: { enemy: 'eclat', every: 3.5, max: 4, offY: -30 },
    },
  ],
  onUpdate: balancer,

  /* ── Les quatre sections ───────────────────────────────────────────────────
     Silhouette ASYMÉTRIQUE, contrairement aux deux boss précédents : deux ailes
     symétriques, un dôme unique au sommet, un socle unique en dessous. Le
     déséquilibre est le premier indice donné au joueur — une pièce sans jumelle
     se remarque, et elle se remarque avant même qu'elle n'ait tiré.

     ⚠️ Ordonnées : le point le plus bas est une aile à offY -70, soit Y = +250
     une fois le porteur posé à +320. */
  parts: [
    { enemy: 'colosse-aile', offX: -210, offY: -70 },
    { enemy: 'colosse-aile', offX: 210, offY: -70 },
    { enemy: 'colosse-dome', offX: 0, offY: 74 },
    { enemy: 'colosse-socle', offX: 0, offY: -46 },
  ],

  /* ⚠️ PAS de `shieldedByParts`. Son absence est la décision structurante du
     combat, pas un oubli — voir l'en-tête du fichier. Le jour où quelqu'un
     l'ajoutera « par cohérence avec les autres boss », il transformera le seul
     boss qui pose un choix en troisième boss qui impose un ordre. */
});
